import { notification } from 'antd';
import getEnv from '../../../../services/util/getEnv';
import * as dashboardService from '../../../services/Dashboard';
import { pushLog2DV2LogServer } from '../../../services/logging';
import {
  getAllMCQChallenges,
  getAllMCQChallenges_V1,
  getAllMCQChallenges_V2,
  getDeveloperRecommendedMCQ,
  getMCQRole,
  updateMCQRole,
  fetchWorkExpMCQInfo,
  checkProfileStatus,
  getTagsMCQFilter
} from '../../services/commonAPI';
import { TestsActions as TAConstants } from './constants';

/**
 * This action gets invoked to fetch the challenge details when `mcq_use_apiv1` flag is turned to OFF.
 * This action should be removed once `mcq_use_apiv1` flag is rolled out to 100% users on Growthbook.
 */
const LOAD_MCQ_CHALLENGES = (dispatch) => {
  dispatch({ type: TestsActions.START_LOADING });
  const requests = [];
  requests.push(
    getDeveloperRecommendedMCQ().then(({ data }) =>
      data.success ? { recommendedMCQ: data.result } : null
    )
  );
  requests.push(
    getAllMCQChallenges({ useUserSkillsAsContext: true }).then(({ data }) =>
      data.success ? { challenges: data.result, stackDemands: data.stackDemands, isTechLead: data.isTechLead } : null
    )
  );
  requests.push(
    getAllMCQChallenges({ useUserSkillsAsContext: false }).then(({ data }) =>
      data.success ? { allChallenges: data.result } : null
    )
  );
  requests.push(
    getMCQRole().then(({ data }) =>
      data.success ? { roles: data.result } : null
    )
  );
  requests.push(
    getMCQRole().then(({ data }) =>
      data.success ? { roles: data.result } : null
    )
  );
  requests.push(
    dashboardService
      .getAllUserProfileData()
      .then((res) => res.data)
      .then(({ code, data }) =>
        code === 200 && data && data.personal_info ? { personal_info: data.personal_info, experience_info: data.experience_info } : null
      )
  );
  requests.push(
    dashboardService
      .getDeveloperSkillInfo()
      .then((res) => res.data)
      .then(({ code, data }) => {
        if (code === 200 && data && data.all_skills) {
          const { all_skills, passed_skills } = data;
          if (Array.isArray(all_skills) && Array.isArray(passed_skills)) {
            return { passed_skills: all_skills.filter(skill => passed_skills.includes(+skill.skill_id)) };
          }
        }
      })
  );
  Promise.all(requests)
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((payload) => {
      if (Object.keys(payload).length === 0) {
        pushLog2DV2LogServer.error('TESTS', 'LOAD_MCQ_CHALLENGES_ERROR', {}, {}, { message: 'Error loading MCQ challenges' });
        openNotification('error', 'Error loading MCQ challenges');
      } else {
        pushLog2DV2LogServer.info('TESTS', 'LOAD_MCQ_CHALLENGES_SUCCESS', {}, payload, null);
        dispatch({
          type: TestsActions.LOAD_MCQ_CHALLENGES_SUCCESS,
          payload
        });
      }
    })
    .catch(catchRequestError('Error loading MCQ challenges', 'LOAD_MCQ_CHALLENGES_ERROR'))
    .finally(() => dispatch({ type: TestsActions.END_LOADING }));
};

/**
 * This action gets invoked to fetch the challenge details when `mcq_use_apiv1` flag is turned to TRUE.
 * It use the most latest version of APIs to get the list of MCQ challenges shown to the users.
 */
const LOAD_MCQ_CHALLENGES_V1 = (dispatch) => {
  dispatch({ type: TestsActions.START_LOADING });
  const requests = [];
  requests.push(
    getDeveloperRecommendedMCQ().then(({ data }) =>
      data.success ? { recommendedMCQ: data.result } : null
    )
  );
  requests.push(
    getAllMCQChallenges_V1({ useUserSkillsAsContext: true }).then(({ data }) =>
      data.success ? { challenges: data.result, stackDemands: data.stackDemands, isTechLead: data.isTechLead } : null
    )
  );
  requests.push(
    getAllMCQChallenges_V1({ useUserSkillsAsContext: false }).then(({ data }) =>
      data.success ? { allChallenges: data.result } : null
    )
  );
  requests.push(
    getMCQRole().then(({ data }) =>
      data.success ? { roles: data.result } : null
    )
  );
  requests.push(
    getMCQRole().then(({ data }) =>
      data.success ? { roles: data.result } : null
    )
  );
  requests.push(
    dashboardService
      .getAllUserProfileData()
      .then((res) => res.data)
      .then(({ code, data }) =>
        code === 200 && data && data.personal_info ? { personal_info: data.personal_info, experience_info: data.experience_info } : null
      )
  );
  requests.push(
    dashboardService
      .getDeveloperSkillInfo()
      .then((res) => res.data)
      .then(({ code, data }) => {
        if (code === 200 && data && data.all_skills) {
          const { all_skills, passed_skills } = data;
          if (Array.isArray(all_skills) && Array.isArray(passed_skills)) {
            return { passed_skills: all_skills.filter(skill => passed_skills.includes(+skill.skill_id)) };
          }
        }
      })
  );
  Promise.all(requests)
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((payload) => {
      if (Object.keys(payload).length === 0) {
        pushLog2DV2LogServer.error('TESTS', 'LOAD_MCQ_CHALLENGES_ERROR', {}, {}, { message: 'Error loading MCQ challenges' });
        openNotification('error', 'Error loading MCQ challenges');
      } else {
        pushLog2DV2LogServer.info('TESTS', 'LOAD_MCQ_CHALLENGES_SUCCESSV1', {}, payload, null);
        dispatch({
          type: TestsActions.LOAD_MCQ_CHALLENGES_SUCCESSV1,
          payload
        });
      }
    })
    .catch(catchRequestError('Error loading MCQ challenges', 'LOAD_MCQ_CHALLENGES_ERROR'))
    .finally(() => dispatch({ type: TestsActions.END_LOADING }));
};

/**
 * This action gets invoked to fetch the challenge details when `use_onboarding_improved_ui_ux_multi_variant flag = v2` flag is turned to TRUE.
 * It use the most latest version of APIs to get the list of MCQ challenges shown to the users.
 */
const LOAD_MCQ_CHALLENGES_V2 = (dispatch) => {
  dispatch({ type: TestsActions.START_LOADING });
  const requests = [];
  requests.push(
    getAllMCQChallenges_V2().then(({ data }) => {
      if (data.success) {
        return {
          challenges: data.result.challenges,
          core_challenges: data.result.core_challenges,
          allChallenges: data.result.all_challenges,
          developer_language_challenges: data.result?.developer_language_challenges
          };
        } else {
          return null;
        }
      })
  );
  requests.push(
    getMCQRole().then(({ data }) =>
      data.success ? { roles: data.result } : null
    )
  );
  requests.push(
    dashboardService
      .getAllUserProfileData()
      .then((res) => res.data)
      .then(({ code, data }) =>
        code === 200 && data && data.personal_info ? { personal_info: data.personal_info, experience_info: data.experience_info } : null
      )
  );
  requests.push(
    dashboardService
      .getDeveloperSkillInfo()
      .then((res) => res.data)
      .then(({ code, data }) => {
        if (code === 200 && data && data.all_skills) {
          const { all_skills, passed_skills } = data;
          if (Array.isArray(all_skills) && Array.isArray(passed_skills)) {
            return { passed_skills: all_skills.filter(skill => passed_skills.includes(+skill.skill_id)) };
          }
        }
      })
  );
  Promise.all(requests)
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((payload) => {
      if (Object.keys(payload).length === 0) {
        pushLog2DV2LogServer.error('TESTS', 'LOAD_MCQ_CHALLENGES_ERROR', {}, {}, { message: 'Error loading MCQ challenges' });
        openNotification('error', 'Error loading MCQ challenges');
      } else {
        pushLog2DV2LogServer.info('TESTS', 'LOAD_MCQ_CHALLENGES_SUCCESSV2', {}, payload, null);
        dispatch({
          type: TestsActions.LOAD_MCQ_CHALLENGES_SUCCESSV2,
          payload
        });
      }
    })
    .catch(catchRequestError('Error loading MCQ challenges', 'LOAD_MCQ_CHALLENGES_ERROR'))
    .finally(() => dispatch({ type: TestsActions.END_LOADING }));
};

const UPDATE_MCQ_ROLE = (roleId, callback) => (dispatch) => {
  dispatch({ type: TestsActions.START_LOADING });

  updateMCQRole(roleId)
    .then((res) => {
      dispatch(TestsActions.LOAD_MCQ_CHALLENGES);
      openNotification('success', 'Role successfully updated');
      if (callback) callback();
    })
    .catch(catchRequestError('Error updating role', 'UPDATE_MCQ_ROLE'))
    .finally(() => dispatch({ type: TestsActions.END_LOADING }));
};

const FETCH_WORK_EXP_INFO = (dispatch) => {
  dispatch({ type: TestsActions.START_WORK_EXP_MCQ_LOADING });
  const challenge_id = getEnv("REACT_APP_WORK_EXP_MCQ") || null;
  if (challenge_id) {
    fetchWorkExpMCQInfo(challenge_id).then((res) => {
      if (res.data.success && res.data.result && res.data.result.timeLeft) {
        const result = res.data.result.timeLeft;
        dispatch({ type: TestsActions.LOAD_WORK_EXP_MCQ, payload: result });
      }
    })
      .catch(catchRequestError('Error fetching Seniority Assessment', 'FETCH_WORK_EXP_INFO'))
      .finally(() => dispatch({ type: TestsActions.END_WORK_EXP_MCQ_LOADING }));
  }
};

const CHECK_PROFILE_STATUS = (dispatch) => {
  checkProfileStatus().then((res) => {
    if (res.data.success && res.data.result.length > 0) {
      const data = res.data.result[0];
      if (data.result > 0) {
        dispatch({ type: TestsActions.LOAD_PROFILE_COMPLETE_STATUS, payload: true });
      } else {
        dispatch({ type: TestsActions.LOAD_PROFILE_COMPLETE_STATUS, payload: false });
      }
    }
  }).catch((e) => console.log(e));
};

const catchRequestError = (message, action) => (error) => {
  console.error(error);
  const { stack, code } = error;
  openNotification('error', message);
  pushLog2DV2LogServer.error('TESTS', action, {}, { message }, { message, stack, code });
};

const openNotification = (type, title, description) => {
  notification[type]({
    message: title,
    description: description,
    duration: 5
  });
};

const LOAD_TAGS_MCQ_FILTER = (dispatch) => {
  dispatch({ type: TestsActions.START_LOADING });
  const requests = [];
  requests.push(
    getTagsMCQFilter().then(({ data }) =>
      data.success ? { tagsMCQFilter: data.result } : null
    )
  );
  Promise.all(requests)
    .then((result) => result.reduce((acc, data) => ({ ...acc, ...data }), {}))
    .then((payload) => {
      if (Object.keys(payload).length === 0) {
        pushLog2DV2LogServer.error('TESTS', 'LOAD_TAGS_MCQ_FILTER_ERROR', {}, {}, { message: 'Error loading Tags MCQ Filter' });
        openNotification('error', 'Error loading Tags MCQ Filter');
      } else {
        pushLog2DV2LogServer.info('TESTS', 'LOAD_TAGS_MCQ_FILTER_SUCCESS', {}, payload, null);
        dispatch({
          type: TestsActions.LOAD_TAGS_MCQ_FILTER_SUCCESS,
          payload
        });
      }
    })
    .catch(catchRequestError('Error loading Tags MCQ Filter', 'LOAD_TAGS_MCQ_FILTER_ERROR'))
    .finally(() => dispatch({ type: TestsActions.END_LOADING }));
};

const TestsActions = {
  ...TAConstants,
  LOAD_MCQ_CHALLENGES,
  LOAD_MCQ_CHALLENGES_V1,
  LOAD_TAGS_MCQ_FILTER,
  UPDATE_MCQ_ROLE,
  FETCH_WORK_EXP_INFO,
  CHECK_PROFILE_STATUS,
  LOAD_MCQ_CHALLENGES_V2
};

export default TestsActions;
